import '@/scss/style.scss';
import messages from '@/utils/locales/messages';
import Maska from 'maska';
import { createPinia } from 'pinia';
import { createApp, watch } from 'vue';
import VueEasyLightbox from 'vue-easy-lightbox';
import { createI18n } from 'vue-i18n';
import VueScrollTo from 'vue-scrollto';
import VueTablerIcons from 'vue-tabler-icons';
import VueApexCharts from 'vue3-apexcharts';
import 'vue3-carousel/dist/carousel.css';
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
import App from './App.vue';
import vuetify from './plugins/vuetify';
import './polyfills';
import router from './router';
// Importar e inicializar NProgress (barra de progresso estilo GitHub)
import { initClarity } from './plugins/clarity';
import './plugins/nprogress';
import { useTenantStore } from '@/stores/tenantStore';

// i18n (legacy: false para permitir useI18n() nos componentes com script setup)
const i18n = createI18n({
    legacy: false,
    locale: 'pt',
    messages: messages,
    silentTranslationWarn: true,
    silentFallbackWarn: true
});

// Create App
const app = createApp(App);

// Plugins essenciais (carregados globalmente)
// Nota: ApexCharts é mantido globalmente pois é leve (~200kb gzipped)
// FullCalendar já é carregado dinamicamente na view Calendar.vue
const pinia = createPinia();
app.use(pinia);
app.use(router);
app.use(vuetify);
app.use(PerfectScrollbarPlugin);
app.use(VueTablerIcons);
app.use(i18n);

// Alinha <html lang> ao i18n: lang="en" com UI em PT fazia o Chrome oferecer tradução e alterar domínios (education → educação).
function syncDocumentHtmlLang(loc: string) {
    const map: Record<string, string> = { pt: 'pt-BR', en: 'en', es: 'es' };
    document.documentElement.lang = map[loc] ?? 'pt-BR';
}
syncDocumentHtmlLang(i18n.global.locale.value);
watch(i18n.global.locale, (l) => syncDocumentHtmlLang(l));

app.use(Maska);
app.use(VueApexCharts, {
    chart: {
        animations: {
            enabled: false
        }
    }
});
app.use(VueEasyLightbox);
app.use(VueScrollTo, {
    duration: 1000,
    easing: 'ease',
    offset: -50
});

// Tratamento de erros global para evitar refresh infinito
import { isBenignBrowserError } from '@/utils/benignBrowserErrors';

let errorLogged = false;
window.addEventListener('error', (event) => {
    // Avisos benignos do navegador (ex.: ResizeObserver loop disparado por menus
    // do Vuetify) chegam como evento `error` mas não podem derrubar a aplicação.
    if (isBenignBrowserError(event.message)) {
        event.preventDefault();
        return;
    }
    if (!errorLogged) {
        errorLogged = true;
        console.error('❌ ERRO GLOBAL CAPTURADO:', {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            error: event.error,
            stack: event.error?.stack
        });

        // Mostrar erro na tela
        const appElement = document.getElementById('app');
        if (appElement) {
            appElement.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif; max-width: 800px; margin: 50px auto;">
          <h1 style="color: #d32f2f;">❌ Erro ao carregar aplicação</h1>
          <p><strong>Mensagem:</strong> ${event.message || 'Erro desconhecido'}</p>
          <p><strong>Arquivo:</strong> ${event.filename || 'N/A'}</p>
          <p><strong>Linha:</strong> ${event.lineno || 'N/A'}:${event.colno || 'N/A'}</p>
          <details style="margin-top: 20px;">
            <summary style="cursor: pointer; color: #1976d2;">Ver stack trace completo</summary>
            <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; overflow: auto; margin-top: 10px; white-space: pre-wrap;">${event.error?.stack || 'N/A'}</pre>
          </details>
          <p style="margin-top: 20px; color: #666;">Por favor, abra o console do navegador (F12) para mais detalhes.</p>
        </div>
      `;
        }
    }
    event.preventDefault(); // Previne refresh automático
    return false; // Previne comportamento padrão
});

window.addEventListener('unhandledrejection', (event) => {
    if (!errorLogged) {
        errorLogged = true;
        console.error('❌ PROMISE REJEITADA NÃO TRATADA:', event.reason);

        // Mostrar erro na tela
        const appElement = document.getElementById('app');
        if (appElement) {
            appElement.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif; max-width: 800px; margin: 50px auto;">
          <h1 style="color: #d32f2f;">❌ Erro ao carregar aplicação</h1>
          <p><strong>Promise rejeitada:</strong></p>
          <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; overflow: auto; white-space: pre-wrap;">${String(event.reason)}</pre>
          <p style="margin-top: 20px; color: #666;">Por favor, abra o console do navegador (F12) para mais detalhes.</p>
        </div>
      `;
        }
    }
    event.preventDefault(); // Previne refresh automático
});

// Inicializar store de autenticação
import { useAuthStore } from '@/stores/authStore';

const authStore = useAuthStore();

// Inicializar autenticação com tratamento de erro
(async () => {
    try {
        console.log('🚀 Iniciando aplicação...');

        // Log de configuração do ambiente (apenas informações não sensíveis)
        const envInfo = {
            VITE_API_URL: import.meta.env.VITE_API_URL || 'NÃO DEFINIDO',
            VITE_APP_ENV: import.meta.env.VITE_APP_ENV || 'NÃO DEFINIDO',
            mode: import.meta.env.MODE || 'NÃO DEFINIDO',
            dev: import.meta.env.DEV,
            prod: import.meta.env.PROD,
            origin: window.location.origin,
            pathname: window.location.pathname
        };
        console.log('⚙️ Configuração do ambiente:', envInfo);

        // Verificar se VITE_API_URL está definida
        if (!import.meta.env.VITE_API_URL) {
            console.warn('⚠️ ATENÇÃO: VITE_API_URL não está definida! O sistema usará fallback "/api"');
        }

        // NÃO inicializar auth aqui - o router já faz isso
        // await authStore.initializeAuth();

        // Multi-tenant: identidade do tenant (título, favicon, cores do tema)
        // aplicada ANTES do mount — evita flash da marca errada. Fallback neutro
        // em falha; nunca a identidade de outro tenant. (specs/001-multi-tenant US3)
        try {
            const { bootstrapTenantBranding } = await import('./plugins/branding');
            await bootstrapTenantBranding();
        } catch (brandingError) {
            console.warn('⚠️ Falha ao aplicar branding do tenant (fallback neutro):', brandingError);
        }

        // Microsoft Clarity POR TENANT: project ID vem do branding já carregado.
        // Como é SPA (sem reload ao navegar), no callback SSO — ou com o token ainda
        // no fragmento — NÃO inicializamos no load: um afterEach one-shot injeta o
        // Clarity assim que a rota sair do callback (o SsoCallback já limpou o #token
        // via router.replace). Sem clarity_project_id no tenant → não injeta nada.
        try {
            // ID do branding do tenant; fallback ao env SÓ para o master (senão um
            // tenant sem Clarity cairia na telemetria do master — vazamento cross-tenant).
            const tStore = useTenantStore();
            const clarityId = tStore.branding.clarity_project_id
                || (tStore.isMaster ? import.meta.env.VITE_CLARITY_PROJECT_ID : undefined);
            const inSsoTokenContext =
                window.location.pathname === '/login/sso-callback' ||
                window.location.hash.includes('token');
            if (inSsoTokenContext) {
                let clarityDone = false;
                const stopClarityGuard = router.afterEach((to) => {
                    if (clarityDone) return;
                    if (to.path === '/login/sso-callback' || window.location.hash.includes('token')) return;
                    clarityDone = true;
                    initClarity(clarityId);
                    stopClarityGuard();
                });
            } else {
                initClarity(clarityId);
            }
        } catch (clarityError) {
            console.warn('⚠️ Falha ao inicializar Microsoft Clarity:', clarityError);
        }

        console.log('📦 Montando app Vue...');

        // Mount com tratamento de erro
        app.mount('#app');
        console.log('✅ App Vue montado com sucesso');
    } catch (error) {
        console.error('❌ Erro ao montar app Vue:', error);
        errorLogged = true;

        // Mostrar erro na tela se o mount falhar
        const appElement = document.getElementById('app');
        if (appElement) {
            appElement.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif; max-width: 800px; margin: 50px auto;">
          <h1 style="color: #d32f2f;">❌ Erro ao carregar aplicação</h1>
          <p><strong>Erro no mount:</strong></p>
          <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; overflow: auto; white-space: pre-wrap;">${String(error)}</pre>
          <p style="margin-top: 20px; color: #666;">Por favor, abra o console do navegador (F12) para ver o stack trace completo.</p>
        </div>
      `;
        }
    }
})();
